/* Folder files (3 Nov 2026, Eugenio's feedback): the files directly inside the selected folder,
 * read as the signed-in person, so only what Drive shows them. Kept in component state only,
 * never stored. Rows are ink, never orange: opening a file is reading, not an action here. */

import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { FolderFile } from '../types'
import { appendUnique, fileKind, fileMeta, type FileKind } from './fileRows'

const MARKS: Record<FileKind, JSX.Element | null> = {
  doc: <path d="M6 8.5h8M6 11h8M6 13.5h5" />,
  sheet: <path d="M5.5 8.5h9v6h-9zM5.5 11.5h9M10 8.5v6" />,
  slides: <path d="M5.5 9h9v5h-9z" />,
  pdf: <path d="M6 14.5V9h1.6a1.4 1.4 0 0 1 0 2.8H6" />,
  image: <path d="M5.5 14.5l3-3.5 2 2 1.5-1.5 2.5 3M12.5 8.8h.01" />,
  video: <path d="M8.5 8.8v5l4-2.5z" />,
  other: null,
}

function FileIcon({ kind }: { kind: FileKind }) {
  return (
    <svg className="ma-ico ma-ico--file" viewBox="0 0 20 20" aria-hidden="true"
      fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 2.5h7.5l3.5 3.5v11.5h-11z M12 2.5V6h3.5" />
      {MARKS[kind]}
    </svg>
  )
}

type Load = { status: 'loading' } | { status: 'error' } | { status: 'ready'; files: FolderFile[]; next: string | null }

export function FolderFiles({ folderId }: { folderId: string }) {
  const [load, setLoad] = useState<Load>({ status: 'loading' })
  const [more, setMore] = useState<'idle' | 'loading' | 'error'>('idle')
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let live = true
    setLoad({ status: 'loading' })
    setMore('idle')
    api.getFolderFiles(folderId)
      .then((r) => { if (live) setLoad({ status: 'ready', files: r.files, next: r.nextPageToken }) })
      .catch(() => { if (live) setLoad({ status: 'error' }) })
    return () => { live = false }
  }, [folderId, attempt])

  const showMore = async () => {
    if (load.status !== 'ready' || !load.next) return
    setMore('loading')
    try {
      const r = await api.getFolderFiles(folderId, load.next)
      setLoad((cur) => cur.status === 'ready' ? { status: 'ready', files: appendUnique(cur.files, r.files), next: r.nextPageToken } : cur)
      setMore('idle')
    } catch {
      setMore('error')
    }
  }

  return (
    <section className="ma-children ma-files" aria-label="Files in this folder" aria-busy={load.status === 'loading'}>
      <h3 className="ma-files__h">
        Files in this folder
        {load.status === 'ready' && load.files.length > 0 && (
          <span className="ma-files__n">{load.files.length}{load.next ? '+' : ''}</span>
        )}
      </h3>
      {load.status === 'loading' ? (
        <ul>
          {[0, 1, 2].map((i) => <li key={i} className="ma-file ma-file--skel"><span className="skeleton" style={{ width: `${60 - i * 12}%` }} /></li>)}
        </ul>
      ) : load.status === 'error' ? (
        <p className="ma-meta">
          Google did not answer. <button className="btn btn--secondary" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
        </p>
      ) : load.files.length === 0 ? (
        <p className="ma-meta">No files directly in this folder.</p>
      ) : (
        <>
          <ul>
            {load.files.map((f) => (
              <li key={f.id} className="ma-file">
                <a className="ma-file__go" href={f.webViewLink} target="_blank" rel="noreferrer" aria-label={`Open in Drive: ${f.name}`}>
                  <FileIcon kind={fileKind(f.mimeType)} />
                  <span className="ma-file__body">
                    <span className="ma-file__nm">{f.name} <span aria-hidden="true">↗</span></span>
                    {fileMeta(f) && <span className="ma-file__meta">{fileMeta(f)}</span>}
                  </span>
                  {f.isShortcut && <span className="ma-tag">Shortcut</span>}
                </a>
              </li>
            ))}
          </ul>
          {load.next && (
            <div className="ma-files__more">
              <button className="btn btn--secondary" onClick={showMore} disabled={more === 'loading'}>
                {more === 'loading' ? 'Loading…' : 'Show more'}
              </button>
              {more === 'error' && <span className="ma-meta">Google did not answer. Try again.</span>}
            </div>
          )}
        </>
      )}
    </section>
  )
}
